import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { UPDATE_FOOD_URL } from '../../config/api';
import { authenticatedFetch, getFirstImageUrl } from '../Utils/apiHelpers';
import { trackFood, trackError } from '../Utils/analytics';
import './EditFoodModal.css';

interface FoodListing {
  food_id: number;
  food_name: string;
  description: string;
  price: number;
  category: string;
  phone_number: string;
  pickup_location: string;
  serving_size: string;
  preparation_time: string;
  available_quantity: number;
  image_urls: string | string[];
}

interface EditFoodModalProps {
  food: FoodListing;
  onClose: () => void;
  onSuccess: () => void;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const EditFoodModal: React.FC<EditFoodModalProps> = ({ food, onClose, onSuccess, showNotification }) => {
  const existingImageUrl = getFirstImageUrl(food.image_urls);
  const isPlaceholder = existingImageUrl.includes('pexels.com');

  const [form, setForm] = useState({
    food_name: food.food_name,
    description: food.description,
    price: String(food.price),
    category: food.category,
    phone_number: food.phone_number,
    pickup_location: food.pickup_location,
    serving_size: food.serving_size,
    preparation_time: food.preparation_time,
    available_quantity: String(food.available_quantity),
    image_url: isPlaceholder ? '' : existingImageUrl,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.food_name.trim()) e.food_name = 'Required';
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0) e.price = 'Must be a positive number';
    if (!form.category) e.category = 'Required';
    if (form.phone_number && !/^[0-9]{10}$/.test(form.phone_number.replace(/\s/g, ''))) e.phone_number = 'Enter a valid 10-digit number';
    if (form.available_quantity && (isNaN(Number(form.available_quantity)) || Number(form.available_quantity) < 0 || !Number.isInteger(Number(form.available_quantity)))) {
      e.available_quantity = 'Must be a non-negative whole number';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // Detect if anything actually changed before sending
    const currentImageUrl = isPlaceholder ? '' : getFirstImageUrl(food.image_urls);
    const noChanges =
      form.food_name.trim() === food.food_name &&
      form.description.trim() === food.description &&
      Number(form.price) === food.price &&
      form.category === food.category &&
      form.phone_number.replace(/\s/g, '') === food.phone_number &&
      form.pickup_location.trim() === food.pickup_location &&
      form.serving_size.trim() === food.serving_size &&
      form.preparation_time.trim() === food.preparation_time &&
      Number(form.available_quantity) === food.available_quantity &&
      form.image_url.trim() === currentImageUrl;

    if (noChanges) {
      showNotification('info', 'No Changes', 'Nothing was changed.');
      onClose();
      return;
    }

    // Always send all fields — API requires a complete body
    const imageUrl = form.image_url.trim() || getFirstImageUrl(food.image_urls);
    const body = {
      food_name: form.food_name.trim(),
      description: form.description.trim(),
      price: Number(form.price),
      category: form.category,
      phone_number: form.phone_number.replace(/\s/g, ''),
      pickup_location: form.pickup_location.trim(),
      serving_size: form.serving_size.trim(),
      preparation_time: form.preparation_time.trim(),
      available_quantity: Number(form.available_quantity),
      image_urls: [imageUrl],
    };

    setIsSubmitting(true);
    try {
      const res = await authenticatedFetch(UPDATE_FOOD_URL(food.food_id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        showNotification('success', 'Food Updated', `${food.food_name} has been updated successfully.`);
        trackFood('food_updated', food.food_name);
        onSuccess();
        onClose();
      } else {
        showNotification('error', 'Update Failed', data.message || 'Failed to update food item.');
        trackError('food_update_failed', 'edit_food_modal');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const field = (id: string, label: string, type: string = 'text', extra?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="efm-group">
      <label htmlFor={id} className="efm-label">{label}</label>
      <input
        id={id}
        type={type}
        className={`efm-input${errors[id] ? ' efm-input--err' : ''}`}
        value={(form as any)[id]}
        onChange={e => handleChange(id, e.target.value)}
        {...extra}
      />
      {errors[id] && <span className="efm-error">{errors[id]}</span>}
    </div>
  );

  return (
    <div className="efm-overlay" onClick={onClose}>
      <div className="efm-modal" onClick={e => e.stopPropagation()}>
        <button className="efm-close" onClick={onClose}><X size={20} /></button>

        <h3 className="efm-title">Edit Food Item</h3>
        <p className="efm-subtitle">Update the details below and save your changes.</p>

        <form onSubmit={handleSubmit} className="efm-form">
          <div className="efm-row">
            {field('food_name', 'Food Name *', 'text', { maxLength: 300 })}
            {field('price', 'Price (₹) *', 'number', { min: '0', step: '0.01' })}
          </div>

          <div className="efm-group">
            <label htmlFor="efm-desc" className="efm-label">Description</label>
            <textarea
              id="efm-desc"
              className="efm-textarea"
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="efm-row">
            <div className="efm-group">
              <label htmlFor="efm-cat" className="efm-label">Category *</label>
              <select
                id="efm-cat"
                className={`efm-input${errors.category ? ' efm-input--err' : ''}`}
                value={form.category}
                onChange={e => handleChange('category', e.target.value)}
              >
                <option value="">Select category</option>
                <option value="veg">Vegetarian</option>
                <option value="non-veg">Non-Vegetarian</option>
              </select>
              {errors.category && <span className="efm-error">{errors.category}</span>}
            </div>
            {field('phone_number', 'Phone Number', 'tel', { maxLength: 10 })}
          </div>

          <div className="efm-row">
            {field('serving_size', 'Serving Size')}
            {field('preparation_time', 'Preparation Time')}
          </div>

          {field('pickup_location', 'Pickup Location')}
          {field('available_quantity', 'Available Quantity', 'number', { min: '0', step: '1' })}

          <div className="efm-group">
            <label htmlFor="efm-img" className="efm-label">Image URL</label>
            <input
              id="efm-img"
              type="url"
              className="efm-input"
              value={form.image_url}
              onChange={e => handleChange('image_url', e.target.value)}
              placeholder="https://..."
            />
            <span className="efm-hint">Leave blank to keep the existing image.</span>
          </div>

          {errors.general && <p className="efm-error efm-error--general">{errors.general}</p>}

          <div className="efm-actions">
            <button type="button" className="efm-btn efm-btn--cancel" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="efm-btn efm-btn--save" disabled={isSubmitting}>
              {isSubmitting ? <><span className="efm-spinner" />Saving...</> : <><Save size={16} />Save Changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFoodModal;
